// Pure helpers that build the metadata object passed to opts.onFrameCapture.
// Extracting these from viewer.js keeps the captureFrame / emitPanoFrame
// closures focused on the canvas + upload work, and gives the metadata shape
// a testable home so subtle drifts (e.g. a stray videoTimeS: 0) get caught.

/** Build the metadata that accompanies a panorama-sourced freeze frame.
 *
 *  videoCurrentTime is the *video element's* currentTime at the moment of
 *  capture. By the time emitPanoFrame runs, openPanoramaAt has already
 *  seeked the video to the panorama's nearest-on-track moment, so the
 *  video's currentTime IS the pano's moment-in-flight expressed in
 *  video-time — exactly what the Saved Frames card, the KMZ balloon, and
 *  any fallback Jump-to need to display and replay.
 */
export function buildPanoFrameMetadata({ panorama, view, videoCurrentTime, utcS }) {
  return {
    source: "panorama",
    panoramaId: panorama.id,
    panoView: view,
    videoTimeS: videoCurrentTime,
    utc: utcS,
    lat: panorama.lat,
    lon: panorama.lon,
  };
}

/** Local wall-clock date and time for the burn-in: `DD/MM/YYYY HH:MM:SS ZONE`.
 *
 *  Day-first because the client reads Australian dates; a 24 h clock with
 *  `hourCycle: "h23"` so midnight is `00`, never `24`; and the zone
 *  abbreviation (AEST / AEDT) so the frame states which clock it is on.
 */
export function fmtLocalDateTime(epochS, tz) {
  const parts = new Intl.DateTimeFormat("en-AU", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hourCycle: "h23", timeZone: tz, timeZoneName: "short",
  }).formatToParts(new Date(epochS * 1000));
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}:${p.second} ${p.timeZoneName}`;
}

const finiteLatLon = (p) => p != null && Number.isFinite(p.lat) && Number.isFinite(p.lon);

/** Pick the position a freeze frame is geotagged with.
 *
 *  The picture's centre on the ground (the footprint boresight) is what the
 *  frame shows, so it wins. Without a usable centre — no sidecar, a clipped
 *  frame, a DEM miss — the aircraft position stands in, and the result says
 *  so, so the burn-in can flag it and a client never mistakes one for the
 *  other. Null when neither is usable.
 *
 *  @returns {{lat:number, lon:number, source:"centre"|"aircraft"}|null}
 */
export function resolveFramePosition({ centre, aircraft }) {
  if (finiteLatLon(centre)) return { lat: centre.lat, lon: centre.lon, source: "centre" };
  if (finiteLatLon(aircraft)) {
    return { lat: aircraft.lat, lon: aircraft.lon, source: "aircraft" };
  }
  return null;
}

/** The lines burnt into a freeze frame's caption box, top to bottom. */
export function buildCaptionLines({ utcS, tz, position, flightId }) {
  const suffix = position?.source === "aircraft" ? " (aircraft)" : "";
  const coord = (v) => (position == null ? "—" : v.toFixed(6) + suffix);
  return [
    fmtLocalDateTime(utcS, tz),
    "LAT   " + coord(position?.lat),
    "LON   " + coord(position?.lon),
    flightId,
  ];
}
